import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, InputDateRangePeriodEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import { usePostHog } from "posthog-js/react";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Box, Button, Dropdown, Grid, Icon, InputDateRange, Modal, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import Markdown from "~/components/Markdown";
import StyledIcon from "~/components/StyledIcon";
import InputMemberSelect from "~/components/InputMemberSelect";
import MemberInsightsReportCreateModal from "~/components/MemberInsightsReportCreateModal";
import integrationSelectors from "~/store/integrations/selectors";
import MemberInsightsConfigureViewDropdown from "~/components/MemberInsightsConfigureViewDropdown";
import MemberInsightsWidgetsGrid from "~/components/MemberInsightsWidgetsGrid";
import memberInsightsSelectors from "~/store/memberInsights/selectors";
import memberInsightsActionCreators from "~/store/memberInsights/actions";
import { DndProvider } from "react-dnd";
import { MemberInsightsActionEnum } from "~/store/memberInsights/types";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { HTML5Backend } from "react-dnd-html5-backend";
import styled from "styled-components";
import useAbacExt from "~/hooks/abac";
import toastsActionCreators from "~/store/toasts/actions";
import { useQueryState } from "~/hooks/common";
import MemberInsightsShareInSlackModal from "~/components/MemberInsightsShareInSlackModal";
const LoadingSection = styled(Section)`
  height: 100vh;
  display: flex;
  align-items: center;
`;
const StyledGrid = styled(Grid)`
  position: sticky;
  top: 0;
  z-index: 2;
    background-color ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.secondary;
}};
`;
const MemberInsightsExplorer = () => {
  const {
    t
  } = useTranslation();
  const posthog = usePostHog();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const insights = useSelector(memberInsightsSelectors.getInsights);
  const insightsStatus = useSelector(memberInsightsSelectors.getInsightsStatus);
  const insightsError = useSelector(memberInsightsSelectors.getInsightsError);
  const sendStatus = useSelector(memberInsightsSelectors.getSendStatus);
  const sendError = useSelector(memberInsightsSelectors.getSendError);
  const view = useSelector(memberInsightsSelectors.getView);
  const filter = useSelector(memberInsightsSelectors.getFilter);
  const action = useSelector(memberInsightsSelectors.getAction);
  const error = insightsError || sendError;
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    userHas
  } = useAbacExt();
  const dispatch = useDispatch();
  useQueryState({
    filter
  }, (_, value) => {
    dispatch(memberInsightsActionCreators.filtered(value));
  });
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (insightsStatus === StatusStateEnum.IDLE) {
      dispatch(memberInsightsActionCreators.exploreStarted({
        filter
      }));
    }
  }, [insightsStatus]);
  if (!insights && error) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
      element: "h1",
      s: {
        mt: 5,
        mb: 8
      },
      className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
    }, t("MemberInsights.memberInsights")), /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 16
      }
    }, /*#__PURE__*/React.createElement(StyledIcon, {
      src: "warningOutline",
      color: "translucentDanger"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 6
      },
      align: "center",
      style: {
        maxWidth: "400px"
      }
    }, t("MemberInsights.reportIssues")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      lineHeight: 6,
      style: {
        maxWidth: "400px"
      },
      align: "center",
      text: t("MemberInsights.reportIssuesInfo")
    })));
  }
  if (!insights) {
    return /*#__PURE__*/React.createElement(LoadingSection, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const handleCreate = () => {
    posthog.capture(`Member Insights – Schedule a Report`);
    dispatch(memberInsightsActionCreators.actionChanged(CRUDActionEnum.CREATE, null));
  };
  const handleFilter = newFilter => {
    posthog.capture(`Member Insights – Filter`, {
      filter: newFilter
    });
    dispatch(memberInsightsActionCreators.filtered({
      ...filter,
      ...newFilter
    }));
  };
  const handleShareInSlack = () => {
    dispatch(memberInsightsActionCreators.actionChanged(MemberInsightsActionEnum.SHARE_IN_SLACK, null));
    posthog.capture(`Member Insights – Share in Slack`);
  };
  const handleSendInSlack = callback => {
    dispatch(memberInsightsActionCreators.sendSlackStarted({
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      memberIds: filter.memberIds
    }, _ref2 => {
      let {
        data
      } = _ref2;
      if (data) {
        dispatch(toastsActionCreators.pushed({
          severity: "success",
          message: t("MemberInsights.reportSent"),
          timeout: 10000
        }));
        callback();
      }
    }));
    posthog.capture(`Member Insights – Send in Slack`);
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mt: 3,
      mb: 8
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("MemberInsights.memberInsights")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error || "",
    linkColor: "text"
  })), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    gap: 3,
    template: "300px 240px auto 1fr auto",
    s: {
      p: 3,
      mx: -3,
      mt: -3,
      mb: 0
    }
  }, /*#__PURE__*/React.createElement(InputDateRange, {
    initialStartDate: filter.dateFrom,
    initialEndDate: filter.dateTo,
    definedPeriods: [InputDateRangePeriodEnum.LAST_DAY, InputDateRangePeriodEnum.LAST_THREE_DAYS, InputDateRangePeriodEnum.CURRENT_WEEK, InputDateRangePeriodEnum.LAST_WEEK, InputDateRangePeriodEnum.LAST_TWO_WEEKS, InputDateRangePeriodEnum.CURRENT_MONTH, InputDateRangePeriodEnum.LAST_MONTH],
    onChange: (dateFrom, dateTo) => handleFilter({
      ...filter,
      dateFrom,
      dateTo
    })
  }), /*#__PURE__*/React.createElement(InputMemberSelect, {
    placeholder: "All members",
    value: filter.memberIds,
    onChange: memberIds => handleFilter({
      ...filter,
      memberIds
    }),
    multiple: true
  }), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement(MemberInsightsConfigureViewDropdown, null))), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(Box, {
    gap: 3,
    direction: "row"
  }, userHas(AuthoritiesEnum.MEMBER_INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Button, {
    s: {
      py: 2.8,
      pl: 3,
      pr: 5
    },
    onClick: handleCreate,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "plus",
      s: {
        mr: 1
      }
    })
  }, "Schedule a Report"), slackIntegration && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    display: "inline-block",
    handlerProps: {
      s: {
        p: 2.8
      },
      color: "translucent",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      display: "block"
    })
  }, _ref3 => {
    let {
      isOpen,
      onItemClick
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, slackIntegration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleSendInSlack(() => {
          onItemClick();
        });
      }
    }, sendStatus === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "send",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("MemberInsights.sendToSlack")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleShareInSlack();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "share",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("MemberInsights.shareInSlack"))));
  }))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(MemberInsightsWidgetsGrid, {
    isLoading: insightsStatus === StatusStateEnum.LOADING,
    view: view,
    widgets: insights.widgets,
    dateFrom: insights.dateFrom,
    dateTo: insights.dateTo,
    memberIds: insights.memberIds,
    s: {
      mt: 2
    }
  })), /*#__PURE__*/React.createElement(Modal, {
    open: action === MemberInsightsActionEnum.SHARE_IN_SLACK
  }, /*#__PURE__*/React.createElement(MemberInsightsShareInSlackModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(MemberInsightsReportCreateModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MEMBER_INSIGHTS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(MemberInsightsExplorer), () => true);